import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircle, Mail, RefreshCw, Send, Pencil, AlertTriangle, CheckCircle2,
} from "lucide-react";

/* Patient communications: what we send, and what actually arrived.
 *
 * WhatsApp and email only — no SMS, which is what the clinic uses. Both
 * transports already existed and carried nothing patient-facing; this is the
 * layer between an event happening and a message going out, plus the log that
 * makes a failure visible. A reminder that silently failed is a patient who
 * silently does not turn up. */

const STATUS_TONE = {
  sent: "bg-green-100 text-green-800",
  delivered: "bg-green-100 text-green-800",
  read: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-gray-200 text-gray-600",
  // "Would have been sent" — messaging is switched off, this is a dry run.
  disabled: "bg-navy/10 text-navy",
  queued: "bg-amber-100 text-amber-800",
  // Not sent: the number belongs to a blacklisted lead (comms.py suppresses it).
  blacklisted: "bg-gray-900 text-white",
};

const CHANNEL_ICON = { whatsapp: MessageCircle, email: Mail };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Communications() {
  const [tab, setTab] = useState("delivery");
  return (
    <div className="space-y-5">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="delivery" data-testid="comms-tab-delivery">Delivery Reports</TabsTrigger>
          <TabsTrigger value="settings" data-testid="comms-tab-settings">What we send</TabsTrigger>
          <TabsTrigger value="templates" data-testid="comms-tab-templates">Templates</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "delivery" && <DeliveryReport />}
      {tab === "settings" && <CommsSettings />}
      {tab === "templates" && <Templates />}
    </div>
  );
}

/* ───────────────────────── Delivery ───────────────────────── */

function DeliveryReport() {
  const today = iso(new Date());
  const [range, setRange] = useState([today, today]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/comms/delivery", { params: { from: range[0], to: range[1] } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    try {
      const r = await api.post(`/comms/resend/${id}`);
      const ok = (r.data.sent || []).some((m) => m.status === "sent");
      ok ? toast.success("Resent") : toast.error("Resend failed — see the row for why");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runNow = async () => {
    try {
      const r = await api.post("/comms/run-reminders");
      toast.success(`Reminders: ${r.data.reminders.sent} sent of ${r.data.reminders.appointments} appointment(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const w = data?.window_status || {};
  const l30 = data?.last_30_days || {};
  const total = Object.values(w).reduce((a, b) => a + b, 0);
  const failed = (w.failed || 0) + (w.skipped || 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <Input type="date" className="h-9 w-36 bg-white" value={range[0]}
                 onChange={(e) => setRange([e.target.value, range[1]])} />
          <span className="pb-2 text-sm text-muted-foreground">to</span>
          <Input type="date" className="h-9 w-36 bg-white" value={range[1]}
                 onChange={(e) => setRange([range[0], e.target.value])} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNow} data-testid="run-reminders-btn">
            <Send className="mr-1.5 h-4 w-4" /> Run today's reminders
          </Button>
          <Button variant="outline" size="icon" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Sent in window" value={total} />
        <Tile label="Delivered" value={(w.sent || 0) + (w.delivered || 0)} tone="text-green-600" />
        <Tile label="Failed / skipped" value={failed} tone={failed ? "text-red-600" : ""} />
        <Tile label="Last 30 days" value={Object.values(l30).reduce((a, b) => a + b, 0)} />
      </div>

      {failed > 0 && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            A WhatsApp message sent by the clinic (rather than replying to a patient) needs a
            Meta-approved template. Until one is approved, WhatsApp sends will fail here —
            the copy is ready, the approval is the blocker.
          </span>
        </p>
      )}

      {loading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : !data?.groups?.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Nothing sent in this window.</p>
      ) : data.groups.map((g) => (
        <div key={g.event} className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
          <p className="border-b border-navy/10 bg-cream/50 px-4 py-2 text-sm font-semibold text-navy">
            {g.label} <span className="text-muted-foreground">({g.messages.length})</span>
          </p>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-navy/5">
              {g.messages.map((m) => {
                const Icon = CHANNEL_ICON[m.channel] || Mail;
                return (
                  <tr key={m.id} data-testid="delivery-row">
                    <td className="w-8 px-3 py-2"><Icon className="h-4 w-4 text-muted-foreground" /></td>
                    <td className="px-1 py-2">
                      <p className="font-medium text-ink">{m.patient_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{m.to}</p>
                    </td>
                    <td className="max-w-md px-3 py-2 text-xs text-muted-foreground">
                      <p className="line-clamp-2">{m.body || m.subject}</p>
                      {m.error && <p className="mt-0.5 font-medium text-red-600">{m.error}</p>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS_TONE[m.status] || STATUS_TONE.queued}`}>
                        {m.status}
                      </span>
                      {/* Which side of the 24-hour window this took: free-form
                          text inside it, an approved template outside. Without
                          this the report can't explain why two identical events
                          read differently on the patient's phone. */}
                      {m.wa_mode && (
                        <p className="mt-1 text-[10px] text-muted-foreground"
                           title={m.wa_template ? `Approved template: ${m.wa_template}` : "Free-form reply inside the 24-hour window"}>
                          {m.wa_mode === "session" ? "in session" : m.wa_template || "template"}
                        </p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      {formatDateTime(m.created_at)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {m.status !== "sent" && (
                        <button onClick={() => resend(m.id)}
                          className="text-xs font-medium text-navy hover:underline">Resend</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Tile({ label, value, tone = "" }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 text-center shadow-sm">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-display text-2xl font-semibold ${tone || "text-navy"}`}>{value ?? 0}</p>
    </div>
  );
}

/* ───────────────────────── Settings ───────────────────────── */

function CommsSettings() {
  const [s, setS] = useState(null);

  const load = useCallback(() => {
    api.get("/comms/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (event, channel, value) => {
    try {
      const r = await api.put("/comms/settings", { events: { [event]: { [channel]: value } } });
      setS((prev) => ({ ...prev, events: r.data.events }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTiming = async (field, value) => {
    try {
      const r = await api.put("/comms/settings", { [field]: Number(value) });
      setS((prev) => ({ ...prev, [field]: r.data[field] }));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!s) return <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      {/* The master switch. Off by default, and the copy has to say plainly
          what "on" means, because the next click contacts real patients. */}
      <div className={`rounded-xl border p-5 shadow-sm ${
        s.enabled ? "border-green-300 bg-green-50/60" : "border-amber-300 bg-amber-50/60"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 font-display text-sm font-semibold text-navy">
              {s.enabled
                ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> Patient messaging is LIVE</>
                : <><AlertTriangle className="h-4 w-4 text-amber-600" /> Patient messaging is off</>}
            </p>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              {s.enabled
                ? "Real patients are being messaged when these events happen."
                : "Nothing is being sent. Messages are still composed and recorded on the "
                  + "Delivery Reports tab, so you can see exactly what would go out — to whom, "
                  + "with the real wording — before switching this on."}
            </p>
          </div>
          <label className="flex shrink-0 items-center gap-2 text-sm font-medium">
            <Switch checked={!!s.enabled} data-testid="comms-master-switch"
                    onCheckedChange={async (v) => {
                      if (v && !window.confirm(
                        "This will start sending WhatsApp and email to real patients "
                        + "whenever these events happen. Continue?")) return;
                      try {
                        const r = await api.put("/comms/settings", { enabled: v });
                        setS((p) => ({ ...p, enabled: r.data.enabled }));
                        toast.success(v ? "Patient messaging is live" : "Patient messaging switched off");
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }} />
            {s.enabled ? "On" : "Off"}
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">When this happens</th>
              <th className="w-32 px-4 py-2.5 text-center font-semibold">WhatsApp</th>
              <th className="w-32 px-4 py-2.5 text-center font-semibold">Email</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/5">
            {Object.entries(s.event_labels || {}).map(([ev, label]) => (
              <tr key={ev} data-testid={`comms-event-${ev}`}>
                <td className="px-4 py-2.5 text-ink">{label}</td>
                {["whatsapp", "email"].map((ch) => (
                  <td key={ch} className="px-4 py-2.5 text-center">
                    <Switch checked={!!s.events?.[ev]?.[ch]}
                            onCheckedChange={(v) => toggle(ev, ch, v)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-navy">Reminder timing</h3>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Days before the appointment</label>
            <Input type="number" min="0" max="7" className="mt-0.5 h-9 w-24"
                   defaultValue={s.reminder_days_before}
                   onBlur={(e) => saveTiming("reminder_days_before", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Send at (hour, IST)</label>
            <Input type="number" min="0" max="23" className="mt-0.5 h-9 w-24"
                   defaultValue={s.reminder_hour}
                   onBlur={(e) => saveTiming("reminder_hour", e.target.value)} />
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          A patient who has opted out of WhatsApp or email is skipped regardless of what is switched on here.
        </p>
      </div>
    </div>
  );
}

/* ───────────────────────── Templates ───────────────────────── */

function Templates() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/comms/templates").then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Placeholders in braces are filled when the message is sent — e.g.{" "}
        <code className="rounded bg-navy/5 px-1">{"{patient_name}"}</code>,{" "}
        <code className="rounded bg-navy/5 px-1">{"{appointment_date}"}</code>,{" "}
        <code className="rounded bg-navy/5 px-1">{"{clinic_phone}"}</code>.
      </p>
      {rows.map((t) => {
        const Icon = CHANNEL_ICON[t.channel] || Mail;
        return (
          <div key={t.id} className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Icon className="h-4 w-4 text-muted-foreground" /> {t.name}
                  <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
                    {t.channel}
                  </span>
                  {t.active === false && (
                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600">Off</span>
                  )}
                </p>
                {t.subject && <p className="mt-1 text-xs font-medium text-muted-foreground">Subject: {t.subject}</p>}
                <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{t.body}</p>
                {(t.buttons || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {t.buttons.map((b) => (
                      <span key={b.id}
                            className="rounded-md border border-navy/15 bg-navy/[0.03] px-2 py-1 text-[11px] font-medium text-navy">
                        {b.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          </div>
        );
      })}
      <TemplateDialog template={editing} onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

// Mirrors jalpi_client.MAX_BUTTONS / MAX_BUTTON_LABEL. The server validates on
// save regardless; these just stop the form producing something it would reject.
const MAX_BUTTONS = 3;
const MAX_BUTTON_LABEL = 20;

function TemplateDialog({ template, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) setForm({
      subject: template.subject || "", body: template.body || "",
      active: template.active !== false,
      // Padded to MAX_BUTTONS so the editor always shows one empty slot to type
      // into. Blank rows are stripped on save — clearing a label removes it.
      buttons: [...(template.buttons || []), ...Array(MAX_BUTTONS).fill(null)]
        .slice(0, MAX_BUTTONS).map((b) => ({ id: b?.id || "", label: b?.label || "" })),
    });
  }, [template]);

  const setButton = (i, label) => setForm((f) => ({
    ...f, buttons: f.buttons.map((b, n) => (n === i ? { ...b, label } : b)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send `buttons` for templates that can carry them — the backend
      // refuses the field on any other event, which would block a plain edit.
      if (template.supports_buttons) {
        payload.buttons = form.buttons.filter((b) => b.label.trim())
          .map((b) => ({ id: b.id || undefined, label: b.label.trim() }));
      } else {
        delete payload.buttons;
      }
      await api.put(`/comms/templates/${template.id}`, payload);
      toast.success("Template saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-navy">{template?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {template?.channel === "email" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject</label>
              <Input className="mt-0.5" value={form.subject || ""}
                     onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Message</label>
            <Textarea className="mt-0.5" rows={7} value={form.body || ""}
                      onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
          </div>
          {template?.supports_buttons && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Reply buttons <span className="font-normal">(optional)</span>
              </label>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Up to {MAX_BUTTONS}, {MAX_BUTTON_LABEL} characters each. They appear
                only when the patient has messaged us in the last 24 hours — outside
                that window WhatsApp allows the words but not the buttons.
              </p>
              <div className="mt-1.5 space-y-1.5">
                {(form.buttons || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={b.label} maxLength={MAX_BUTTON_LABEL}
                           placeholder={i === 0 ? "Book Appointment" : "Leave empty to remove"}
                           onChange={(e) => setButton(i, e.target.value)} />
                    <span className={`w-10 shrink-0 text-right text-[11px] tabular-nums ${
                      b.label.length >= MAX_BUTTON_LABEL ? "text-amber-600" : "text-muted-foreground"}`}>
                      {b.label.length}/{MAX_BUTTON_LABEL}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
